import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { EditorState } from 'lexical';
import { useEffect, useMemo, useRef } from 'react';
import { MarkdownImage } from 'src/company/templates/hooks/useConvertToMarkdownMutation';
import { createTransformers } from '../config/transformers';
import { $getEditorValue, replaceImagesWithPlaceholders } from '../utils';

type OnChangePluginProps = {
  onChange?: (value: string) => void;
  onDirty?: () => void;
  placeholderImages: MarkdownImage[];
};

export function OnChangePlugin({ onChange, onDirty, placeholderImages }: OnChangePluginProps) {
  const [editor] = useLexicalComposerContext();
  const onChangeRef = useRef(onChange);
  const onDirtyRef = useRef(onDirty);
  const placeholderImagesRef = useRef(placeholderImages);
  const isFirstUpdateRef = useRef(true);
  const transformers = useMemo(() => createTransformers(editor), [editor]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onDirtyRef.current = onDirty;
  }, [onDirty]);

  useEffect(() => {
    placeholderImagesRef.current = placeholderImages;
  }, [placeholderImages]);

  useEffect(() => {
    const removeTextContentListener = editor.registerTextContentListener(() => {
      if (!isFirstUpdateRef.current) {
        onDirtyRef.current?.();
      }
    });

    return removeTextContentListener;
  }, [editor]);

  useEffect(() => {
    const removeListener = editor.registerUpdateListener(
      ({ editorState }: { editorState: EditorState }) => {
        if (isFirstUpdateRef.current) {
          isFirstUpdateRef.current = false;
          return;
        }

        const onChangeCallback = onChangeRef.current;
        if (!onChangeCallback) return;

        editorState.read(() => {
          const markdown = $getEditorValue(transformers);
          const withPlaceholders = replaceImagesWithPlaceholders(
            markdown,
            placeholderImagesRef.current,
          );
          onChangeCallback(withPlaceholders);
        });
      },
    );

    return () => {
      removeListener();
    };
  }, [editor, transformers]);

  return null;
}
